import { useState } from 'react'
import { useLang } from '@/hooks/use-lang'
import { t } from '@/lib/i18n'

function tokenizeJSON(json: string): { type: string; value: string }[] {
  const tokens: { type: string; value: string }[] = []
  let i = 0

  while (i < json.length) {
    const ch = json[i]

    // Whitespace / structural characters
    if (ch === ' ' || ch === '\n' || ch === '\r' || ch === '\t') {
      let ws = ''
      while (i < json.length && (json[i] === ' ' || json[i] === '\n' || json[i] === '\r' || json[i] === '\t')) {
        ws += json[i++]
      }
      tokens.push({ type: 'whitespace', value: ws })
      continue
    }

    // Brackets & punctuation
    if (ch === '{' || ch === '}' || ch === '[' || ch === ']' || ch === ':' || ch === ',') {
      tokens.push({ type: 'punctuation', value: ch })
      i++
      continue
    }

    // String
    if (ch === '"') {
      let str = '"'
      i++
      while (i < json.length) {
        const c = json[i]
        str += c
        if (c === '\\') {
          i++
          if (i < json.length) { str += json[i]; i++ }
          continue
        }
        if (c === '"') { i++; break }
        i++
      }
      // Check if next non-whitespace is a colon → it's a key
      let j = i
      while (j < json.length && (json[j] === ' ' || json[j] === '\t')) j++
      if (json[j] === ':') {
        tokens.push({ type: 'key', value: str })
      } else {
        tokens.push({ type: 'string', value: str })
      }
      continue
    }

    // Number
    if (ch === '-' || (ch >= '0' && ch <= '9')) {
      let num = ''
      while (i < json.length && /[-+0-9.eE]/.test(json[i])) {
        num += json[i++]
      }
      tokens.push({ type: 'number', value: num })
      continue
    }

    // true / false / null
    if (json.startsWith('true', i)) {
      tokens.push({ type: 'boolean', value: 'true' })
      i += 4
      continue
    }
    if (json.startsWith('false', i)) {
      tokens.push({ type: 'boolean', value: 'false' })
      i += 5
      continue
    }
    if (json.startsWith('null', i)) {
      tokens.push({ type: 'null', value: 'null' })
      i += 4
      continue
    }

    // Fallback: unknown character
    tokens.push({ type: 'unknown', value: ch })
    i++
  }

  return tokens
}

/*
  Warm, near-monochrome syntax palette on the brand ink ladder. Green
  (--accent-ink, the AA-legible green-as-text) is reserved for string VALUES so
  the eye lands on the data; keys are muted ink, numbers/booleans differ by
  weight rather than hue, punctuation recedes. No second accent, no decorative
  amber/red (Status-Only Rule) — the payload reads as "Calm Workshop".
*/
const tokenClass: Record<string, string> = {
  key: 'text-[color:var(--fg-2)]',
  string: 'text-[color:var(--accent-ink)]',
  number: 'text-[color:var(--fg)]',
  boolean: 'text-[color:var(--fg)] font-semibold',
  null: 'text-[color:var(--fg-4)] italic',
  punctuation: 'text-[color:var(--fg-4)]',
  whitespace: '',
  unknown: 'text-[color:var(--fg-3)]',
}

export function JsonViewer({ data }: { data: string }) {
  useLang()
  const [copied, setCopied] = useState(false)
  // Default to horizontal scroll (compact, preserves indentation); the Wrap
  // toggle soft-wraps long lines for narrow / mobile reading.
  const [wrap, setWrap] = useState(false)

  let formatted: string
  let isValid = true
  try {
    formatted = JSON.stringify(JSON.parse(data), null, 2)
  } catch {
    formatted = data
    isValid = false
  }

  function handleCopy() {
    navigator.clipboard.writeText(formatted)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const tokens = isValid ? tokenizeJSON(formatted) : null

  return (
    <div className="relative rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
      <div className="px-4 py-2 flex items-center justify-between gap-2" style={{ backgroundColor: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
        <span className="text-xs font-medium" style={{ color: 'var(--muted)' }}>JSON</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setWrap(w => !w)}
            aria-pressed={wrap}
            aria-label={t('toggleLineWrap')}
            className="text-xs transition inline-flex items-center justify-center px-2 py-1 tap text-[color:var(--muted)] hover:text-[color:var(--text)] focus-visible:text-[color:var(--text)]"
            style={{ color: wrap ? 'var(--text)' : undefined }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M3 12h15a3 3 0 0 1 0 6h-4" />
              <polyline points="16 16 14 18 16 20" />
              <line x1="3" y1="18" x2="10" y2="18" />
            </svg>
          </button>
          <button
            type="button"
            onClick={handleCopy}
            className="text-xs transition inline-flex items-center justify-center px-2 py-1 tap text-[color:var(--muted)] hover:text-[color:var(--text)] focus-visible:text-[color:var(--text)]"
          >
            {copied ? t('copied') : t('copy')}
          </button>
        </div>
      </div>
      <pre
        className={`p-4 text-xs font-mono max-h-96 leading-relaxed ${wrap ? 'overflow-y-auto whitespace-pre-wrap break-all' : 'overflow-auto'}`}
        style={{ backgroundColor: 'var(--bg)' }}
      >
        {tokens ? (
          tokens.map((tok, idx) => (
            tok.type === 'whitespace'
              ? tok.value
              : <span key={idx} className={tokenClass[tok.type] ?? ''}>{tok.value}</span>
          ))
        ) : (
          <span style={{ color: 'var(--text)' }}>{formatted}</span>
        )}
      </pre>
    </div>
  )
}
